/**
 * The pinned reference note for an earlier quest (Tome UI Round 2 and 3, the next program's E2): its
 * description, giver (with the atlas link), listed rewards for the character's class, and its own
 * earlier quests. Following one of those adds it to the trail; Back returns along it. A reference
 * is read-only: it never accepts, discovers or rewards anything.
 */
import React from 'react';
import { TomeButton, TomeChip } from '../../primitives/tome';
import { resolveQuestEndNpcDisplay, UNKNOWN_QUEST_END_NPC_NAME } from '../../../../shared/quests/questEndNpcDisplay';
import { NpcQuestRewards } from '../../Systems/NPC/NpcQuestRewards';
import { questCardView } from '../../npc/hydrateNpcDialogue';
import { JournalAtlasLink } from './JournalAtlasLink';
import { questDefinition, type JournalQuest } from './questJournalModel';

export function referenceQuest(id: string, completedIds: readonly string[]): JournalQuest {
  const definition = questDefinition(id);
  return {
    id, title: definition?.name ?? 'Unknown quest', description: definition?.description ?? '',
    state: completedIds.includes(id) ? 'completed' : 'locked', featured: definition?.featured, definition,
  };
}

export const earlierQuestLabel = (q: JournalQuest) => `${q.title}, ${q.state === 'completed' ? 'completed' : 'not completed'} — show details`;

interface JournalReferenceProps {
  /** Quest ids from the one opened on the page to the one shown (last). */
  trail: readonly string[];
  completedIds: readonly string[];
  playerClass?: string;
  onFollow: (id: string) => void;
  onBack: () => void;
  onClose: () => void;
}

export const JournalReference: React.FC<JournalReferenceProps> = ({ trail, completedIds, playerClass, onFollow, onBack, onClose }) => {
  const shown = referenceQuest(trail[trail.length - 1], completedIds);
  const previous = trail.length > 1 ? referenceQuest(trail[trail.length - 2], completedIds) : null;
  const giver = resolveQuestEndNpcDisplay(shown.definition?.startNpc).name;
  // The codex's reward line for this class; listed rewards, not a receipt of what was received.
  const view = questCardView(shown.id, undefined, playerClass);
  const earlier = shown.definition?.requiredQuests ?? [];
  return <aside className="quest-journal-reference" aria-label={`${shown.title} reference`}>
    <header>
      <TomeChip tone={shown.state === 'completed' ? 'completed' : 'locked'}>{shown.state === 'completed' ? 'Completed' : 'Earlier quest'}</TomeChip>
      {previous && <button type="button" className="quest-journal-reference-back" onClick={onBack}
        aria-label={`Back to ${previous.title}`}>‹ {previous.title}</button>}
      <TomeButton tone="quiet" aria-label="Close reference" onClick={onClose}>✕</TomeButton>
    </header>
    <h3>{shown.title}</h3>
    {shown.description && <p>{shown.description}</p>}
    {giver !== UNKNOWN_QUEST_END_NPC_NAME && <p className="quest-journal-reference-meta">Given by <strong>{giver}</strong>
      <JournalAtlasLink npcId={shown.definition?.startNpc} role="reference-giver" /></p>}
    {earlier.length > 0 && <p className="quest-journal-reference-meta">{earlier.map((id) => {
      const q = referenceQuest(id, completedIds);
      return <span key={id}>Earlier quest:{' '}
        <button type="button" className="quest-journal-prereq" aria-label={earlierQuestLabel(q)} onClick={() => onFollow(id)}>{q.title}</button>
        {' '}<small>{q.state === 'completed' ? 'Completed' : 'Not completed'}</small></span>;
    })}</p>}
    {view && <div className="quest-journal-reference-rewards"><NpcQuestRewards quest={view} label="Listed rewards" /></div>}
  </aside>;
};
